import type {
  ArchitectureContext,
  ArchitectureSnapshot,
} from "./architecture-model";
import type { DiagramRecipe, DiagramFocus, DiagramKind } from "./diagram-types";

export type ResponseIntent = "explain" | "diagram";
export interface TurnOptions {
  alreadyDisplayed?: boolean;
  delegationId?: string;
  architecture?: ArchitectureContext;
  diagramMode?: "documented" | "what-if";
  responseIntent?: ResponseIntent;
}

/** A request owns its scope even while identity/session creation is pending. */
export function captureTurnScope(
  context: ArchitectureContext | undefined,
  mode: DiagramRecipe["mode"],
  responseIntent?: ResponseIntent,
  discussion?: DiagramRecipe,
  exploration?: { focus: DiagramFocus; kind: DiagramKind; displayLevel?: 'overview' | 'technical' },
) {
  const architecture = context ? structuredClone(context) : undefined;
  if (
    discussion &&
    (!architecture ||
      discussion.catalog.catalogId !== architecture.catalogId ||
      discussion.catalog.revision !== architecture.revision ||
      discussion.catalog.sha256 !== architecture.sha256 ||
      discussion.catalog.phase !== architecture.phase)
  )
    throw new Error(
      "This saved diagram uses another phase or catalogue revision. Select its phase and update a copy before discussing it.",
    );
  return {
    architecture,
    ...(architecture
      ? {
          diagramRequest: {
            mode,
            scope: "selected" as const,
            ...((discussion ? discussion.displayLevel : exploration?.displayLevel) ? { displayLevel: discussion ? discussion.displayLevel : exploration?.displayLevel } : {}),
            ...(exploration
              ? {
                  focus: structuredClone(exploration.focus),
                  kind: exploration.kind,
                }
              : {}),
            ...(responseIntent ? { responseIntent } : {}),
            ...(discussion
              ? { kind: discussion.kind, diagramId: discussion.id }
              : {}),
          },
        }
      : {}),
  };
}

export function scopeKey(context?: ArchitectureContext) {
  return JSON.stringify(context ?? null);
}

export function diagramMatchesSnapshot(
  diagram: DiagramRecipe,
  snapshot?: ArchitectureSnapshot,
) {
  return Boolean(
    snapshot &&
    diagram.catalog.catalogId === snapshot.catalog.catalogId &&
    diagram.catalog.revision === snapshot.catalog.revision &&
    diagram.catalog.sha256 === snapshot.sha256,
  );
}

export const SAFE_VOICE_FAILURE =
  "I couldn't complete that answer. Please try again, or ask a shorter question. Your existing document is still available.";
